import { Caption, Card, VAccordionContent, VAccordionItem, VAccordionTrigger } from '@vrzl/ui';

import type { ValidationReportItem } from '@/domains/report/api/types';

import { matchesFilter } from '../utils/filterCriteria';
import type { CriterionGroupCounts } from '../utils/groupCriteria';

import { CriterionRow } from './criterionRow';
import { GroupStatusIndicators } from './groupStatusIndicators';

type CriterionGroupProps = {
  groupKey: string;
  name: string;
  /** Counts over the whole group, independent of filter and search. */
  counts: CriterionGroupCounts;
  /** Items left after filter and search. */
  items: ValidationReportItem[];
  eventId: string;
  /** Ids of the criteria that got feedback in this session. */
  sentFeedback: ReadonlySet<string>;
  onFeedbackSent: (criterionId: string) => void;
};

// Fixed widths (table-fixed): long values must wrap instead of pushing Feedback
// and Status out of view. "Waarde in documenten" takes the remaining width.
const COLUMNS = [
  { label: 'Criteria', width: 'w-[25%]' },
  { label: 'Waarde in documenten', width: '' },
  { label: 'Bron', width: 'w-36' },
  { label: 'Feedback', width: 'w-25' },
  { label: 'Status', width: 'w-36' },
] as const;

export function CriterionGroup({
  groupKey,
  name,
  counts,
  items,
  eventId,
  sentFeedback,
  onFeedbackSent,
}: CriterionGroupProps) {
  // Present criteria first, the ones that are not present at the bottom of the same table.
  const rows = [
    ...items.filter((item) => matchesFilter(item, 'present')),
    ...items.filter((item) => matchesFilter(item, 'missing')),
  ];

  return (
    <VAccordionItem value={groupKey} className="border-b-0">
      <Card padding="none" className="overflow-hidden">
        <div className="relative">
          {/* Radix renders an <h3> heading; CardTitle's classes because an h3 cannot nest in it. */}
          <VAccordionTrigger className="cursor-pointer items-center rounded-none px-5 py-4 text-base font-semibold text-foreground hover:no-underline focus-visible:ring-inset">
            <span className="flex flex-1 flex-wrap items-center gap-3">
              <span className="flex-1">{name}</span>
              <Caption as="span">
                {counts.present} van {counts.total} aanwezig
              </Caption>
              {/* Reserves the room of the two indicators below, so the counts line up across groups. */}
              <span aria-hidden="true" className="w-16 shrink-0" />
            </span>
          </VAccordionTrigger>

          <div className="absolute inset-y-0 right-14 flex items-center gap-1.5">
            <GroupStatusIndicators counts={counts} />
          </div>
        </div>

        <VAccordionContent className="pb-0">
          {rows.length > 0 && (
            <div className="border-t border-border">
              <table className="w-full table-fixed">
                <thead>
                  <tr>
                    {COLUMNS.map((column) => (
                      <th
                        key={column.label}
                        scope="col"
                        className={`px-5 py-2 text-left ${column.width}`}
                      >
                        <Caption as="span" className="font-medium uppercase tracking-wide">
                          {column.label}
                        </Caption>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <CriterionRow
                      key={item.id}
                      item={item}
                      eventId={eventId}
                      feedbackSent={sentFeedback.has(item.id)}
                      onFeedbackSent={onFeedbackSent}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </VAccordionContent>
      </Card>
    </VAccordionItem>
  );
}
